import { Components, Theme } from '@mui/material/styles'
import { tokens } from './tokens'

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme: Theme) => ({
      ':root': {
        '--primary-main': tokens.colors.light.primary.main,
        '--primary-light': tokens.colors.light.primary.light,
        '--primary-dark': tokens.colors.light.primary.dark,
      },
      '*': {
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
        // Global scrollbar styling - ensuring these are well-defined for all elements
        '&::-webkit-scrollbar': {
          width: 4,
          height: 4,
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-track': {
          background: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: `${theme.palette.primary.main}4D`, // 30% opacity
          borderRadius: 4,
          '&:hover': {
            backgroundColor: `${theme.palette.primary.main}80`, // 50% opacity
          },
        },
        // Firefox scrollbar styling
        scrollbarWidth: 'thin',
        scrollbarColor: `${theme.palette.primary.main}4D transparent`,
        // IE scrollbar styling
        msOverflowStyle: 'auto',
      },
      // Dark mode scrollbar styles
      '@media (prefers-color-scheme: dark)': {
        '*': {
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: `${theme.palette.primary.main}4D`,
            '&:hover': {
              backgroundColor: `${theme.palette.primary.main}80`,
            },
          },
        },
      },
      body: {
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      // Default behavior - only show scrollbars when content overflows
      '.MuiBox-root, .MuiDrawer-paper, .MuiDialog-paper, .MuiPopover-paper, .MuiMenu-paper': {
        '&[data-scrollbar-auto="true"]': {
          overflow: 'auto',
        },
        '&[data-scrollbar-hover="true"]': {
          overflow: 'hidden',
          '&:hover': {
            overflow: 'auto',
          },
          scrollbarGutter: 'stable',
        },
      },
    }),
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
      },
    },
  },
  MuiButton: {
    variants: [
      {
        props: { variant: 'cd-outlined' },
        style: ({ theme }) => ({
          position: 'relative',
          border: 'none',
          color: theme.palette.text.primary,
          fontWeight: 500,
          padding: '10px 24px',
          borderRadius: theme.shape.borderRadius,
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            padding: 2,
            borderRadius: theme.shape.borderRadius,
            background: theme.palette.gradient.primary,
            WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
            WebkitMaskComposite: 'xor',
            maskComposite: 'exclude',
          },
          '&:hover': {
            background: theme.palette.action.hover,
            '&::before': {
              opacity: 0.8,
            },
          },
        }),
      },
      {
        props: { variant: 'outlined', size: 'small' },
        style: ({ theme }) => ({
          color: theme.palette.text.secondary,
          borderColor: theme.palette.divider,
          textTransform: 'none',
          padding: '6px 16px',
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: 1.5,
          '&:hover': {
            backgroundColor: 'transparent',
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
          },
          '&.Mui-disabled': {
            borderColor: theme.palette.divider,
            color: theme.palette.text.disabled,
          },
        }),
      },
    ],
    styleOverrides: {
      root: {
        padding: '10px 24px',
        textTransform: 'none',
        fontWeight: 500,
      },
      contained: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
          outline: 'none',
        },
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
      },
      outlined: ({ theme }) => ({
        '&:hover': {
          backgroundColor: 'transparent',
        },
        '&.map-toggle-button, &.generate-report-button': {
          borderColor: theme.palette.divider,
          color: theme.palette.text.secondary,
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: 'transparent',
            color: theme.palette.primary.main,
          },
          '&.Mui-disabled': {
            borderColor: theme.palette.divider,
            color: theme.palette.text.disabled,
          },
        },
      }),
    },
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        '&.Mui-focusVisible': {
          outline: 'none',
          boxShadow: 'none',
        },
        '&:focus': {
          outline: 'none',
          boxShadow: 'none',
        },
      },
    },
  },
  MuiTypography: {
    styleOverrides: {
      root: ({ theme }) => ({
        '&.gradient-text': {
          background: theme.palette.gradient.primary,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          color: 'transparent',
        },
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 2,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        '&.Mui-selected': {
          fontWeight: 600,
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        height: '100%',
        backgroundColor:
          theme.palette.mode === 'dark' ? theme.palette.overlay.dark.card : theme.palette.overlay.light.card,
        borderRadius: theme.shape.borderRadius,
        transition: theme.animation.transitions.all,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxShadow:
          theme.palette.mode === 'dark'
            ? `0 0 0 1px ${theme.palette.divider}`
            : `0 2px 4px rgba(0, 0, 0, 0.05), 0 0 0 1px ${theme.palette.divider}`,
      }),
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(2),
        '&:last-child': {
          paddingBottom: theme.spacing(2),
        },
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between',
      }),
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        transition: theme.animation.transitions.backgroundColor,
      }),
    },
  },
  MuiChip: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        transition: theme.animation.transitions.all,
      }),
      sizeSmall: {
        height: 24,
        fontSize: '0.7rem',
      },
      outlined: ({ theme }: { theme: Theme }) => ({
        '&.MuiChip-colorPrimary': {
          borderColor: `${theme.palette.primary.main}40`,
        },
        '&.MuiChip-colorSecondary': {
          borderColor: `${theme.palette.secondary.main}40`,
        },
      }),
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: ({ theme }) => ({
        borderRadius: theme.shape.borderRadius * 2,
        overflow: 'hidden',
      }),
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: '16px 20px 8px',
        '& h2': {
          fontSize: '1.25rem',
          fontWeight: 600,
        },
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: '8px 20px 16px',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '0 20px 20px',
        gap: '12px',
      },
    },
  },
}
